import { getLinkForBrandByName } from "~/common/branding";
import { GraphQLServerError } from "~/common/graphql/GraphQLServerError";
import { commonResx } from "~/resxDist/commonResx";
import { errorsResx } from "~/resxDist/errorsResx";
import { reloadLocation } from "~/utils/urlUtils";
import { apiMiddlewareActionTypes } from "../Middleware/apiMiddlewareActionTypes";
import { ApiErrorTypes, ErrorHandlingOptions, ObjectNotFoundErrorBody, UserFriendlyExcBody } from "./errorTypes";
import {
    getErrorType,
    getResponseBody,
    isGatewayTimeoutError,
    isGraphQlError,
    isJQueryXhr,
    isObjectNotFoundError,
    isOfflineError,
    isServerVersionMismatchError,
    isUpgradePlanRequiredError,
    isUserFriendlyError,
} from "./errorUtils";

export type ObjectNotFoundAction = ReturnType<typeof objectNotFoundApiError>;
export type UserFriendlyErrorAction = ReturnType<typeof userFriendlyApiError>;
export type UnknownErrorAction = ReturnType<typeof unknownApiError>;

export type HandlingConfiguration = (errorType: ApiErrorTypes, body: any) => ErrorHandlingOptions;

const getOptions = (configuration?: HandlingConfiguration, errorType?: ApiErrorTypes, body?: any) =>
    configuration?.(errorType, body) || undefined;

export const createApiErrorAction = (
    xhr: Partial<JQuery.jqXHR> | GraphQLServerError,
    requestData?: any,
    configuration?: HandlingConfiguration
) => {
    const body = getResponseBody(xhr);
    const errorType = getErrorType(xhr);
    const options = getOptions(configuration, errorType, body);

    if (isObjectNotFoundError(xhr)) {
        return objectNotFoundApiError(body as ObjectNotFoundErrorBody, requestData, options);
    }

    if (isUserFriendlyError(xhr)) {
        return userFriendlyApiError(body as UserFriendlyExcBody, requestData, options);
    }

    if (
        errorType === ApiErrorTypes.UnknownError ||
        errorType === ApiErrorTypes.ForbiddenError ||
        errorType === ApiErrorTypes.InvalidParameters ||
        (isJQueryXhr(xhr) &&
            (xhr.status === 404 /* not found */ ||
                xhr.status === 403 /* forbidden */ ||
                xhr.status === 400)) /* bad request */ ||
        isOfflineError(xhr) ||
        isGatewayTimeoutError(xhr) ||
        isServerVersionMismatchError(xhr) ||
        (isGraphQlError(xhr) && xhr.isUnknownError)
    ) {
        return unknownApiError(requestData, {
            message:
                (isOfflineError(xhr) && commonResx.OfflineErrorMessage) ||
                (isGatewayTimeoutError(xhr) && commonResx.TimeoutErrorMessage) ||
                (isServerVersionMismatchError(xhr) && commonResx.error_reload),
            ...options,
        });
    }

    if (isUpgradePlanRequiredError(xhr)) {
        return upgradePlanRequiredError(requestData, options);
    }

    return null;
};

export const objectNotFoundApiError = (
    body: ObjectNotFoundErrorBody,
    requestData?: any,
    options?: ErrorHandlingOptions
) =>
    ({
        type: apiMiddlewareActionTypes.API_OBJECT_NOT_FOUND,
        body,
        requestData,
        ...options,
    } as const);

export const userFriendlyApiError = (body: UserFriendlyExcBody, requestData?: any, options?: ErrorHandlingOptions) =>
    ({
        type: apiMiddlewareActionTypes.API_USER_FRIENDLY_EXC,
        body,
        requestData,
        ...options,
    } as const);

export const unknownApiError = (requestData?: any, options?: ErrorHandlingOptions) =>
    ({
        type: ApiErrorTypes.UnknownError,
        requestData,
        ...options,
        callback: options?.callback || (() => reloadLocation()),
    } as const);

export const upgradePlanRequiredError = (requestData?: any, options?: ErrorHandlingOptions) =>
    unknownApiError(requestData, {
        message: errorsResx.upgradeToEnterpriseMessage.replace("{licenseUrl}", getLinkForBrandByName("hostedPricing")),
        ...options,
    });

export function parseApiError(e: Partial<JQuery.jqXHR> | GraphQLServerError, configuration?: HandlingConfiguration) {
    let errorAction = createApiErrorAction(e, undefined, configuration);

    if (!errorAction) {
        ep.common.redirectToErrorPageIfProvided(getResponseBody(e));

        // re-throw in development/testing mode
        if (process.env.NODE_ENV !== "production") {
            throw e;
        }

        return;
    }

    return errorAction;
}
